import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useState } from 'react';

import { AudienceJobStatus, useAudienceOptions, type AudienceOptionType } from '@/api/audience';
import { useCurrentInstance } from '@/api/organization';
import {
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxStatus,
  ComboboxTrigger,
  ComboboxValue,
  DebouncedCombobox,
  InfiniteComboboxList,
  type ComboboxProps,
} from '@/components/Form/pickers';
import LetteredAvatar from '@/components/LetteredAvatar';
import { Text } from '@/components/typography';
import { numberFormat } from '@/helper/numberFormatter';
import { cn } from '@/lib/utils';
import { COLOR_PRIMARY } from '@/styles/palette';

type Props = ComboboxProps<AudienceOptionType, false> & {
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  error?: Error | null;
  value?: AudienceOptionType;
  isLoading?: boolean;
  prependedOptions?: AudienceOptionType[];
  showSizeInOption?: boolean;
  onBlur?: () => void;
};

const AudienceSelect = ({
  className,
  value,
  'aria-invalid': ariaInvalid,
  isLoading: isLoadingProp,
  prependedOptions,
  showSizeInOption,
  disabled = false,
  size = 'large',
  placeholder = t`Select Audience...`,
  error,
  ...rest
}: Props) => {
  const [search, setSearch] = useState('');
  const { instance } = useCurrentInstance();

  const { audienceOptions, hasMore, isLoading, isFetchingNextPage, fetchNextPage } =
    useAudienceOptions({ search, prependedOptions });
  const tamAudience = instance?.tam?.audience;

  const isTAMOption = (optionId: string | number) => {
    return optionId === '' || (tamAudience && optionId === tamAudience.id);
  };

  return (
    <DebouncedCombobox
      itemToStringValue={(item) => item.id}
      itemToStringLabel={(item) => item.name ?? ''}
      isItemEqualToValue={(item, selected) => item.id === selected.id}
      isLoading={isLoadingProp || isLoading || isFetchingNextPage}
      error={error}
      disabled={disabled}
      filteredItems={audienceOptions ?? []}
      size={size}
      value={value ?? null}
      onSearch={setSearch}
      {...rest}
    >
      <ComboboxTrigger
        className={className}
        data-testid="audience-dropdown"
        aria-invalid={ariaInvalid}
      >
        {value && isTAMOption(value.id) ? (
          <span className="flex min-w-0 items-center gap-2">
            <LetteredAvatar
              className="shrink-0"
              name="T"
              color={COLOR_PRIMARY}
              shape="circle"
              size="small"
              disabled={disabled}
            />
            <ComboboxValue placeholder={placeholder} />
          </span>
        ) : (
          <ComboboxValue placeholder={placeholder} />
        )}
      </ComboboxTrigger>
      <ComboboxContent>
        <ComboboxInput showTrigger={false} placeholder={t`Search...`} />
        <ComboboxStatus />
        <ComboboxEmpty>
          <Trans>No audiences were found...</Trans>
        </ComboboxEmpty>
        <InfiniteComboboxList
          fetchNextPage={fetchNextPage}
          hasMore={hasMore}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
        >
          {(item: AudienceOptionType) => {
            const { id, name, metrics, lastJob } = item;
            const isTAM = isTAMOption(id);
            const hasNoSize = !metrics || !metrics.size;
            const isProcessing = lastJob?.status === AudienceJobStatus.PROCESSING;
            const isOptionDisabled = hasNoSize || isProcessing;

            return (
              <ComboboxItem key={id} value={item} disabled={isOptionDisabled}>
                <div className={cn('flex w-full flex-col gap-1', isOptionDisabled && 'opacity-30')}>
                  {isTAM ? (
                    <div className="flex items-center gap-2">
                      <LetteredAvatar name="T" color={COLOR_PRIMARY} shape="circle" size="small" />
                      <Text className="truncate" variant="body1">
                        {name}
                      </Text>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-2">
                      <Text className="truncate" variant="body1" title={name}>
                        {name}
                      </Text>
                      {!showSizeInOption && isOptionDisabled && (
                        <Text variant="caption1" weight="light" color="grey">
                          {isProcessing ? (
                            <Trans>Processing</Trans>
                          ) : (
                            hasNoSize && <Trans>Empty</Trans>
                          )}
                        </Text>
                      )}
                    </div>
                  )}
                  {showSizeInOption && metrics && (
                    <Text variant="caption1" weight="light" color="grey">
                      {isProcessing ? (
                        <Trans>Processing</Trans>
                      ) : (
                        metrics?.size != null && <Trans>Count: {numberFormat(metrics.size)}</Trans>
                      )}
                    </Text>
                  )}
                </div>
              </ComboboxItem>
            );
          }}
        </InfiniteComboboxList>
      </ComboboxContent>
    </DebouncedCombobox>
  );
};

export default AudienceSelect;
